// CanvasView マウント時に RealtimeClient を作成し、サーバからの op を doc store へ反映する。
// authMode === "mock" のときは mock realtime（自分が送った op をエコーバック）を使い、
// それ以外では WebSocket 経由で実サーバに接続する。

import { useEffect, useMemo, useState } from "react";
import { useShowToast } from "@meiden-connect/ui";
import { getWebConfig } from "../env";
import { createMockRealtimeClient } from "./mock-realtime";
import { createWebSocketRealtimeClient } from "./websocket-realtime";
import { useCanvasDocStore } from "../features/canvas/store/canvas-doc-store";
import { useCanvasViewersStore } from "../features/canvas/store/canvas-viewers-store";
import { useCanvasTransientStore } from "../features/canvas/store/canvas-transient-store";
import type { RealtimeClient } from "./realtime-client";
import type { CanvasObject, CanvasOp, ServerMessage } from "@refinement-canvas/domain";
import { useCanvasApi } from "../lib/canvas-api";

interface State {
  readonly client: RealtimeClient | null;
  readonly status: "idle" | "connecting" | "connected" | "error";
  readonly error?: Error | undefined;
}

// 予期せぬ切断からの再接続パラメータ。指数バックオフで再試行し、上限到達時のみ
// エラー状態へ落として既存の全画面リロード復旧へ委ねる。
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 15_000;
const RECONNECT_MAX_ATTEMPTS = 8;
// 再接続成功後にこの時間が経過するまで、次の切断通知を抑制する（ms）。
// 短時間の再切断でトーストがスタックしないよう、クールダウンを設ける。
const RECONNECT_TOAST_COOLDOWN_MS = 30_000;

export function useRealtimeConnection(canvasId: string): State {
  const config = getWebConfig();
  const canvasApi = useCanvasApi();
  const onServerCommit = useCanvasDocStore((s) => s.onServerCommit);
  const onServerReject = useCanvasDocStore((s) => s.onServerReject);
  const loadSnapshot = useCanvasDocStore((s) => s.loadSnapshot);
  const setViewers = useCanvasViewersStore((s) => s.setViewers);
  const setCursor = useCanvasViewersStore((s) => s.setCursor);
  const setViewerView = useCanvasViewersStore((s) => s.setView);
  const resetViewers = useCanvasViewersStore((s) => s.reset);
  const showToast = useShowToast();

  // クライアント自身は再生成したくないので useMemo + キーで安定させる
  const client = useMemo<RealtimeClient>(() => {
    if (config.authMode === "mock") {
      return createMockRealtimeClient();
    }
    return createWebSocketRealtimeClient({ websocketUrl: config.realtimeWebSocketUrl });
  }, [config.authMode, config.realtimeWebSocketUrl]);

  const [status, setStatus] = useState<State["status"]>("idle");
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setStatus("connecting");

    // サーバからの message を doc store / viewers store に橋渡しする
    const unsubscribe = client.onMessage((message: ServerMessage) => {
      switch (message.type) {
        case "op.commit":
          onServerCommit(message.op, message.serverSeq);
          // 確定 op が届いたら、対応する操作中ゴースト（一時プレビュー）を破棄して
          // 確定済みオブジェクトとゴーストが二重表示されるのを防ぐ。
          clearTransientForCommittedOp(message.op);
          return;
        case "op.reject":
          onServerReject(message.clientOpId);
          showToast(
            "warning",
            message.reason || "操作を保存できませんでした。必要に応じて新しいキャンバスへ分割してください。"
          );
          // 楽観更新済みの表示をサーバ権威状態へ戻すため、拒否時は最新スナップショットを取得する。
          void (async () => {
            try {
              const data = await canvasApi.fetchCanvasBootstrap(canvasId);
              if (cancelled) return;
              if (data.canvasId !== canvasId) return;
              loadSnapshot({
                canvasId: data.canvasId,
                serverSeq: data.serverSeq,
                objects: [...data.objects],
                meta: data.meta,
              });
            } catch (e) {
              if (cancelled) return;
              console.warn("[realtime] op.reject 後の bootstrap に失敗", e);
            }
          })();
          return;
        case "bootstrap.snapshot":
          loadSnapshot({
            canvasId,
            serverSeq: message.serverSeq,
            objects: message.objects as CanvasObject[],
          });
          return;
        case "viewer.snapshot":
          setViewers(message.viewers);
          return;
        case "viewer.cursor":
          if (message.cursor) {
            setCursor(message.userId, {
              xRatio: message.cursor.xRatio,
              yRatio: message.cursor.yRatio,
              updatedAt: Date.now(),
            });
          } else {
            setCursor(message.userId, null);
          }
          return;
        case "viewer.view":
          // フォーカスモード用の他者ビュー更新。永続化しない情報なので最新値だけを保持する。
          if (message.view) {
            setViewerView(message.userId, {
              panX: message.view.panX,
              panY: message.view.panY,
              zoom: message.view.zoom,
              updatedAt: Date.now(),
            });
          } else {
            setViewerView(message.userId, null);
          }
          return;
        case "op.transient": {
          // 他参加者の操作中プレビュー（ゴースト）。永続化せず transient store に反映し、
          // canvas-remote-previews-layer がリアルタイム描画する。
          const transient = useCanvasTransientStore.getState();
          if (message.op.type === "object.transient.transform") {
            transient.applyRemoteTransform(message.fromUserId, message.op);
          } else if (message.op.type === "pen.stroke.append") {
            transient.appendRemotePen(message.fromUserId, message.op);
          }
          // object.text.cursor は本レイヤでは扱わない（テキスト編集カーソルは別経路）。
          return;
        }
        case "resync.required":
          // サーバ再起動 / シーケンス欠落時は REST bootstrap で最新スナップショットを取り直す。
          // 画面遷移や canvas 切替が走った後にレスポンスが返るケースを考慮し、
          // useEffect の cleanup で立てた cancelled フラグと、レスポンスの canvasId が
          // 現在表示中の canvasId と一致しているかを二重確認してから store を上書きする。
          console.warn("[realtime] resync.required:", message.reason);
          void (async () => {
            try {
              const data = await canvasApi.fetchCanvasBootstrap(canvasId);
              if (cancelled) return;
              if (data.canvasId !== canvasId) return;
              loadSnapshot({
                canvasId: data.canvasId,
                serverSeq: data.serverSeq,
                objects: [...data.objects],
                meta: data.meta,
              });
            } catch (e) {
              if (cancelled) return;
              console.warn("[realtime] resync 後の bootstrap に失敗", e);
            }
          })();
          return;
      }
    });

    // 1 回分の接続処理。required 環境では都度フレッシュなチケットを発行する
    // （チケットは短命なため、再接続のたびに新しいものが必要）。
    // チケット発行・接続の各 await 後に cancelled を再確認し、effect の cleanup 後に
    // ソケットを張ってしまう（受信・close 購読も無いゴースト接続のリーク）のを防ぐ。
    const connectOnce = async (): Promise<void> => {
      let ticket: string | undefined;
      if (config.authMode !== "mock") {
        const session = await canvasApi.issueRealtimeSession(canvasId);
        ticket = session.token;
      }
      // チケット発行待ちの間に unmount / canvas 切替で cleanup 済みなら、接続を張らない。
      if (cancelled) return;
      await client.connect(canvasId, ticket);
      // 接続確立後に取消されていた場合は、張ったソケットを即座に閉じて破棄する。
      if (cancelled) {
        client.disconnect();
      }
    };

    // 再接続成功後、切断中に取りこぼしたサーバ op を反映するため最新スナップショットを取り直す。
    // doc store は同一 canvas なら未 ack の楽観 op（inFlightOps）を再適用して保持するため、
    // 編集途中のローカル変更は失われない。
    // ここで失敗を握りつぶすと「取りこぼし未回収のまま接続済み」に見えてサイレントな同期欠落が
    // 残るため、例外はそのまま投げて再接続処理（reconnect）側の再試行 / エラー遷移に委ねる。
    const rebootstrap = async (): Promise<void> => {
      const data = await canvasApi.fetchCanvasBootstrap(canvasId);
      if (cancelled || data.canvasId !== canvasId) return;
      loadSnapshot({
        canvasId: data.canvasId,
        serverSeq: data.serverSeq,
        objects: [...data.objects],
        meta: data.meta,
      });
    };

    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // 1 回の切断エピソードでトーストを多重表示しないためのフラグ。
    let disconnectNotified = false;
    // 最後の再接続成功から RECONNECT_TOAST_COOLDOWN_MS が経過するまで、次の切断通知を抑制する期限。
    // 成功トーストを表示したかどうかに関わらず、再接続成功のたびに更新される。
    let reconnectToastCooldownUntil = 0;
    // 再接続試行の世代。新しい切断（onClose）が来るたびに +1 し、進行中の古い試行を無効化する。
    // これにより、再接続中にさらに切断された場合でも、古い試行が成功扱いになったり
    // 再接続タイマーが多重化したりするのを防ぐ。
    let reconnectEpoch = 0;

    const clearReconnectTimer = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    // 次の再接続を 1 件だけ予約する。試行回数をここで increment し、上限に達したらエラー状態へ
    // 落として既存の全画面リロード復旧へ委ねる。reconnectAttempts は「最後に再接続が完全成功して
    // から積み上がった連続試行数」で、完全成功時のみ 0 へ戻す（onClose では戻さない）。これにより
    // 「open 後・rebootstrap 完了前にさらに切断」を繰り返しても上限到達でフォールバックでき、
    // 無限再接続ループ・送信キュー肥大に陥らない。常に最新の試行だけが残るよう既存タイマーは消す。
    const scheduleReconnect = (): void => {
      if (cancelled) return;
      clearReconnectTimer();
      reconnectAttempts += 1;
      if (reconnectAttempts > RECONNECT_MAX_ATTEMPTS) {
        // 再接続が継続的に失敗／切断し続けるときは、エラー状態にして全画面リロード復旧へ委ねる。
        setStatus("error");
        setError(new Error("共同編集の再接続が繰り返し失敗しました。"));
        return;
      }
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempts - 1),
        RECONNECT_MAX_DELAY_MS
      );
      reconnectTimer = setTimeout(() => {
        void reconnect();
      }, delay);
    };

    // 予約された再接続を 1 回試みる。成功（connectOnce＋rebootstrap）で完了、失敗時は再予約する。
    async function reconnect(): Promise<void> {
      if (cancelled) return;
      // この試行の世代を固定する。await の合間に新しい切断（onClose）が来て世代が進んだら、
      // この試行は陳腐化したものとして以降の状態更新・再スケジュールを行わない。
      const epoch = reconnectEpoch;
      try {
        await connectOnce();
        if (cancelled || epoch !== reconnectEpoch) return;
        // 取りこぼし回収（再 bootstrap）が成功して初めて「再接続完了」とみなす。
        // ここで失敗すれば下の catch で再予約し、connect() が既存ソケットを静かに張り替えるため
        // 二重接続にはならない。成功トーストも回収成功後に出す。
        await rebootstrap();
        if (cancelled || epoch !== reconnectEpoch) return;
        // 切断中に行った編集は、WebSocket クライアントの送信待ちキュー（永続 op は破棄せず、
        // 送信途中の切断でも残す）に積まれており、再接続の open 時に送り直される。ここで
        // doc store の inFlightOps を一括再送することはしない——inFlightOps には「作成者だけに
        // 見せるローカル専用 op（送信しない attachment.update など）」が含まれ得るうえ、サーバは
        // clientOpId による重複排除を行わないため、キュー送信分との二重送信で create/commit 系が
        // 偽の reject を起こすため。なお「接続中に送信した直後・ack 前にネットワーク断で当該 op が
        // サーバ未達のまま失われる」稀なケースは、本フローでは回収できない既知の残存制限である
        // （元実装は再接続自体が無く全 op を喪失していたため、本対応で大幅に改善している）。
        // 完全な回収にはサーバ側の clientOpId 冪等化と送信側 ack 追跡の導入が必要で、別対応とする。
        reconnectAttempts = 0; // 完全成功時のみリセット
        setStatus("connected");
        // 再接続成功のたびにクールダウンを延長する（disconnectNotified の値に関わらず更新）。
        // こうすることで、クールダウン中に再切断→再接続が繰り返されても期限が都度リセットされ
        // 30 秒超後のフラップで問題が再発するのを防ぐ。
        reconnectToastCooldownUntil = Date.now() + RECONNECT_TOAST_COOLDOWN_MS;
        if (disconnectNotified) {
          disconnectNotified = false;
          showToast("success", "共同編集に再接続しました。");
        }
      } catch (e) {
        // 陳腐化した試行は何もしない（最新試行が別途進行している）。
        if (cancelled || epoch !== reconnectEpoch) return;
        // connectOnce / rebootstrap の失敗。次の試行を予約する（回数 increment と上限判定は内部で行う）。
        scheduleReconnect();
      }
    }

    // 接続確立後の予期せぬ切断を検知して再接続を開始する。
    // 切断中もキャンバスは表示したまま（status は connected のまま）、背後で再接続する。
    const unsubscribeClose = client.onClose(() => {
      if (cancelled) return;
      // 新しい切断エピソード。世代を進めて進行中の古い試行を無効化し、再接続を予約する。
      // reconnectAttempts はここでは 0 に戻さない（完全成功時のみ戻す）。これにより
      // 「open 後・rebootstrap 完了前の再切断」が続いても上限到達でフォールバックできる。
      reconnectEpoch += 1;
      if (!disconnectNotified && Date.now() >= reconnectToastCooldownUntil) {
        disconnectNotified = true;
        showToast("info", "共同編集の接続が切れました。再接続しています…");
      }
      scheduleReconnect();
    });

    void (async () => {
      try {
        // 初回接続。チケット発行に失敗した場合は接続できないため、エラー扱いとする。
        await connectOnce();
        if (cancelled) return;
        setStatus("connected");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      unsubscribe();
      unsubscribeClose();
      client.disconnect();
      resetViewers();
      useCanvasTransientStore.getState().reset();
    };
  }, [
    canvasApi,
    canvasId,
    client,
    config.authMode,
    loadSnapshot,
    onServerCommit,
    onServerReject,
    setViewers,
    setCursor,
    resetViewers,
    showToast,
  ]);

  return { client, status, error };
}

// 確定 op に対応する一時プレビュー（ゴースト）を破棄する。
// - transform: 確定させた本人のゴーストのみ破棄（別ユーザーが同じオブジェクトを操作中の
//   新しいゴーストを誤って消さない）
// - delete: オブジェクト自体が消えるため、誰のゴーストでも破棄
// - create: 採番済みオブジェクトの id でゴースト枠を破棄
// - pen.stroke.commit: 確定ストロークの id（=描画中 strokeId）のゴーストのみ破棄
function clearTransientForCommittedOp(op: CanvasOp): void {
  const transient = useCanvasTransientStore.getState();
  switch (op.type) {
    case "object.transform":
      transient.clearTransformsForObject(op.objectId, op.actorUserId);
      return;
    case "object.delete":
      transient.clearTransformsForObject(op.objectId);
      return;
    case "object.create":
      transient.clearTransformsForObject(op.object.id);
      return;
    case "pen.stroke.commit":
      // 確定オブジェクト ID は描画中 strokeId と一致するため、該当ストロークだけを破棄する。
      transient.clearPenStroke(op.object.id);
      return;
    default:
      return;
  }
}
